import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import type { Enums } from "@/integrations/supabase/types";

type AppRole = Enums<"app_role">;

export function useUserRole() {
  const { user } = useAuth();
  const { organization } = useOrganization();

  const { data: roles = [], isLoading, isError } = useQuery({
    queryKey: ["user-roles", user?.id, organization?.id],
    queryFn: async () => {
      if (!user || !organization) return [];
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("organization_id", organization.id);
      if (error) throw error;
      return (data ?? []).map((r) => r.role as AppRole);
    },
    enabled: !!user && !!organization,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60_000, // 5 min — role doesn't change often
    gcTime: 30 * 60_000,   // keep in cache 30 min
  });

  return {
    roles,
    isAdmin: roles.includes("admin"),
    isLoading: !!user && isLoading,
    isError,
  };
}
